// import { Deploy } from 'cordova-plugin-ionic/dist/ngx';

import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule, REMOVE_STYLES_ON_COMPONENT_DESTROY } from '@angular/platform-browser';
import { Router, RouteReuseStrategy } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FaConfig, FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { far } from '@fortawesome/pro-regular-svg-icons';
import { fal } from '@fortawesome/pro-light-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { LeafletMarkerClusterModule } from '@bluehalo/ngx-leaflet-markercluster';
import { QuillModule } from 'ngx-quill';
import { NgSelectModule } from '@ng-select/ng-select';
import { IonicStorageModule, Storage } from '@ionic/storage-angular';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// https://angular.io/guide/i18n#i18n-pipes
import { DatePipe, registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import localeTurk from '@angular/common/locales/tr';
import localeFrench from '@angular/common/locales/fr';
import localePolska from '@angular/common/locales/pl';
import localeBosnian from '@angular/common/locales/bs';
import localeCroatian from '@angular/common/locales/hr';
import localeSerbian from '@angular/common/locales/sr';
import localeRomanian from '@angular/common/locales/ro';
import localeCzech from '@angular/common/locales/cs';
import localeSlovakia from '@angular/common/locales/sk';
import localeUkraine from '@angular/common/locales/uk';

// for datepicker and everything using momentjs - should be transitioned to datefns at some point
import 'moment/locale/de.js';
import 'moment/locale/tr.js';
import 'moment/locale/fr.js';
import 'moment/locale/pl.js';
import 'moment/locale/bs.js';
import 'moment/locale/hr.js';
import 'moment/locale/sr.js';
import 'moment/locale/ro.js';
import 'moment/locale/cs.js';
import 'moment/locale/sk.js';
import 'moment/locale/uk.js';

/**
 * Initialize Sentry for error tracking
 * --- Start
 */
import { ApiAuthenticationInterceptor } from './core/http-interceptors/api-auth-interceptor';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import * as SentryAngular from '@sentry/angular';
import { MsalBroadcastService, MsalGuard, MsalInterceptor, MsalModule, MsalRedirectComponent, MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { CalendarModule, DateAdapter as CalDateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { CustomMomentDateAdapter } from './custom-mat-date-adapter';

import { providePrimeNG } from 'primeng/config';
import Lara from '@primeng/themes/lara';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TRANSLATIONS, DEFAULT_LANG } from './core/translations';
import { LocaleId } from './core/locale/locale-id.service';
import { LocaleSwitcherService } from './core/locale/locale-switcher.service';

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}
// the order of injected services must match the given order further down in the module configuration
export function appInitializerFactory(
    _traceService: SentryAngular.TraceService,
    storage: Storage,
    translate: TranslateService,
    localeSwitcherService: LocaleSwitcherService
) {
    return () =>
        new Promise<any>(async (resolve: any) => {
            // this language will be used as a fallback when a translation isn't found in the current language
            translate.setDefaultLang(DEFAULT_LANG);

            try {
                await storage.create();
                let savedLang = await storage.get('dp_chosen_lang');
                let browserLanguage = translate.getBrowserLang() || DEFAULT_LANG;

                browserLanguage = browserLanguage.substring(0, 2).toLowerCase();
                let language = TRANSLATIONS.some((x) => x.code == browserLanguage) ? browserLanguage : DEFAULT_LANG;

                // var language = this.getSuitableLanguage(browserLanguage);

                if (savedLang && savedLang != undefined && savedLang != '' && savedLang != language) {
                    translate.use(savedLang);
                    await storage.set('dp_chosen_lang', savedLang);
                    localeSwitcherService.setLocale(savedLang);
                } else {
                    translate.use(language);
                    await storage.set('dp_chosen_lang', language);
                    localeSwitcherService.setLocale(language);
                }
                resolve(null);
            } catch (ex) {
                console.warn('Language detection failed. Using default language!', ex);
                localeSwitcherService.setLocale(DEFAULT_LANG);
                resolve(null);
            }
        });
}

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent, MsalRedirectComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        IonicModule.forRoot(),
        IonicStorageModule.forRoot(),
        AppRoutingModule,
        CoreModule,
        FontAwesomeModule,
        SweetAlert2Module.forRoot(),
        LeafletModule,
        LeafletMarkerClusterModule,
        QuillModule.forRoot(),
        NgSelectModule,
        MatMomentDateModule,
        CalendarModule.forRoot({ provide: CalDateAdapter, useFactory: adapterFactory }),
        MsalModule.forRoot(
            new PublicClientApplication({
                // MSAL Configuration
                auth: {
                    clientId: environment.AZ_CLIENT_ID,
                    authority: environment.AZ_AUTHORITY,
                    redirectUri: '/auth',
                },
                cache: {
                    cacheLocation: BrowserCacheLocation.LocalStorage,
                    storeAuthStateInCookie: true, // set to true for IE 11
                },
                system: {
                    allowNativeBroker: false,
                    loggerOptions: {
                        loggerCallback: () => {},
                        piiLoggingEnabled: false,
                    },
                },
            }),
            {
                interactionType: InteractionType.Popup, // MSAL Guard Configuration
            },
            { interactionType: InteractionType.Popup, protectedResourceMap: new Map() }
        ),
        EditorModule,
        TranslateModule.forRoot({
            defaultLanguage: DEFAULT_LANG,
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
    ],
    providers: [
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        { provide: ErrorHandler, useValue: SentryAngular.createErrorHandler() },
        { provide: SentryAngular.TraceService, deps: [Router] },
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFactory,
            deps: [SentryAngular.TraceService, Storage, TranslateService, LocaleSwitcherService],
            multi: true,
        },
        { provide: HTTP_INTERCEPTORS, useClass: ApiAuthenticationInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
        {
            provide: LOCALE_ID,
            useClass: LocaleId,
            deps: [LocaleSwitcherService],
        },
        DatePipe,
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
        { provide: REMOVE_STYLES_ON_COMPONENT_DESTROY, useValue: true },
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: Lara,
                options: {
                    darkModeSelector: '.darkmode',
                },
            },
        }),
        { provide: DateAdapter, useClass: CustomMomentDateAdapter },
    ],
})
export class AppModule {
    constructor(library: FaIconLibrary, faConfig: FaConfig) {
        // configure fontawesome
        library.addIconPacks(far, fal, fab);
        faConfig.fixedWidth = true;
        faConfig.defaultPrefix = 'far';

        // Register Angular Locales
        registerLocaleData(localeEn, 'en');
        registerLocaleData(localeDe, 'de');
        registerLocaleData(localeTurk, 'tr');
        registerLocaleData(localeFrench, 'fr');
        registerLocaleData(localePolska, 'pl');
        registerLocaleData(localeBosnian, 'bs');
        registerLocaleData(localeCroatian, 'hr');
        registerLocaleData(localeSerbian, 'sr');
        registerLocaleData(localeRomanian, 'ro');
        registerLocaleData(localeCzech, 'cs');
        registerLocaleData(localeSlovakia, 'sk');
        registerLocaleData(localeUkraine, 'uk');
    }
}
